import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../shared/formatMoney';
import { TYPE_LABELS } from './driverTypes';
import DriverNav from './DriverNav';
import { HiTruck, HiCurrencyDollar, HiExclamationCircle, HiIdentification, HiClipboardCheck } from 'react-icons/hi';

const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const expiryColor = dateStr => {
    if (!dateStr) return 'text-gray-400';
    const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (days < 0) return 'text-red-600 font-semibold';
    if (days <= 30) return 'text-orange-500 font-semibold';
    return 'text-gray-700';
};

const DriverDashboard = () => {
    const { user } = useAuth();
    const driverId = user?.linked_entity_id;
    const [driver, setDriver] = useState(null);
    const [systemWeek, setSystemWeek] = useState(null);
    const [allocation, setAllocation] = useState(null);
    const [recentTxns, setRecentTxns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!driverId) return;
        const load = async () => {
            setLoading(true);
            setError(null);

            const now = new Date();
            const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const pad = n => String(n).padStart(2, '0');
            const dateFrom = `${firstOfMonth.getFullYear()}-${pad(firstOfMonth.getMonth() + 1)}-${pad(firstOfMonth.getDate())}`;
            const dateTo   = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

            const [driverRes, weeksRes, txnRes, allocRes] = await Promise.allSettled([
                axios.get(`${rootUrl}/drivers/${driverId}`),
                axios.get(`${rootUrl}/api/system/valid_weeks`),
                axios.get(`${rootUrl}/drivers/${driverId}/transactions`, { params: { date_from: dateFrom, date_to: dateTo, order: 'desc' } }),
                axios.get(`${rootUrl}/cars/drivers/driver/${driverId}`),
            ]);

            if (driverRes.status === 'fulfilled') {
                setDriver(driverRes.value.data);
            } else {
                setError('Failed to load driver profile.');
                setLoading(false);
                return;
            }

            if (weeksRes.status === 'fulfilled') {
                const raw = weeksRes.value.data?.system_week;
                if (raw) setSystemWeek(raw.split('_')[1]);
            }

            if (txnRes.status === 'fulfilled') {
                setRecentTxns(txnRes.value.data?.data || []);
            }

            if (allocRes.status === 'fulfilled') {
                const allocs = allocRes.value.data?.data || [];
                setAllocation(allocs.find(a => !a.end_date) || null);
            }

            setLoading(false);
        };
        load();
    }, [driverId]);

    if (!driverId) return (
        <div className="min-h-screen flex items-center justify-center text-gray-500 pb-20">
            No driver account linked.
        </div>
    );

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center pb-20">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    const monthLabel = new Date().toLocaleDateString('en-KE', { month: 'long', year: 'numeric' });

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="bg-orange-500 px-4 pt-10 pb-8">
                <p className="text-orange-100 text-sm">Welcome back,</p>
                <h1 className="text-white text-2xl font-bold mt-0.5">{driver?.name || user?.name}</h1>
                {driver?.id_no && (
                    <p className="text-orange-100 text-sm mt-1">ID: {driver.id_no}</p>
                )}
            </div>

            <div className="px-4 -mt-4 space-y-4">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-600 text-sm">
                        <HiExclamationCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {!error && (
                    <>
                        {/* Period + Balance */}
                        <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between">
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide">PERIOD</p>
                                <p className="text-sm font-semibold text-gray-800 mt-0.5">{monthLabel}</p>
                                {systemWeek && (
                                    <p className="text-xs text-gray-400 mt-0.5">Week {systemWeek}</p>
                                )}
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-400 uppercase tracking-wide">BALANCE</p>
                                <p className="text-lg font-bold text-orange-500 mt-0.5">{formatMoney(driver?.bal ?? 0)}</p>
                                <p className="text-xs text-gray-400 mt-0.5">current</p>
                            </div>
                        </div>

                        {/* License details */}
                        <div className="bg-white rounded-2xl shadow-sm p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <HiIdentification className="w-5 h-5 text-orange-500" />
                                <h2 className="font-semibold text-gray-700 text-sm">License</h2>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                <span className="text-gray-400 text-xs">Number</span>
                                <span className="text-gray-800 text-xs font-medium">{driver?.license_no || '—'}</span>
                                <span className="text-gray-400 text-xs">Expiry</span>
                                <span className={`text-xs ${expiryColor(driver?.license_expiry)}`}>{fmt(driver?.license_expiry)}</span>
                            </div>
                        </div>

                        {/* Current Car + Inspections */}
                        <div className="bg-white rounded-2xl shadow-sm p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <HiTruck className="w-5 h-5 text-orange-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    {allocation ? (
                                        <>
                                            <p className="font-bold text-gray-800">{allocation.plate_no}</p>
                                            <p className="text-xs text-gray-500 truncate">{allocation.car_make} {allocation.car_model}</p>
                                        </>
                                    ) : (
                                        <p className="text-gray-400 text-sm">No car allocated</p>
                                    )}
                                </div>
                            </div>
                            {allocation && (
                                <div className="border-t border-gray-50 pt-3">
                                    <div className="flex items-center gap-1 mb-2">
                                        <HiClipboardCheck className="w-4 h-4 text-gray-400" />
                                        <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Inspections</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                        <span className="text-gray-400">Internal due</span>
                                        <span className={expiryColor(allocation.next_internal_inspection_due)}>{fmt(allocation.next_internal_inspection_due)}</span>
                                        <span className="text-gray-400">Govt (external) due</span>
                                        <span className={expiryColor(allocation.next_government_inspection_due)}>{fmt(allocation.next_government_inspection_due)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* This Month transactions */}
                        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                            <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <HiCurrencyDollar className="w-5 h-5 text-orange-500" />
                                    <h2 className="font-semibold text-gray-700 text-sm">This Month</h2>
                                </div>
                                <span className="text-xs text-gray-400">{monthLabel}</span>
                            </div>
                            {recentTxns.length === 0 ? (
                                <p className="text-gray-400 text-sm text-center py-6">No activity this month.</p>
                            ) : (
                                <ul className="divide-y divide-gray-50">
                                    {recentTxns.map((txn, i) => {
                                        return (
                                            <li key={txn.id || i} className="px-4 py-3 flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-800">{TYPE_LABELS[txn.type] || txn.type}</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">{fmt(txn.date)}</p>
                                                    {txn.your_ref && <p className="text-xs text-gray-600 mt-0.5">{txn.your_ref}</p>}
                                                </div>
                                                <span className={`text-sm font-semibold flex-shrink-0 ${Number(txn.amount) < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                                                    {formatMoney(txn.amount)}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </>
                )}
            </div>

            <DriverNav />
        </div>
    );
};

export default DriverDashboard;
